<template>
  <div id="container">
    <div class="column">
      <multitoggle
        v-model="selectedType"
        :options="byOptions"
      />
      <ul
        v-if="selectedType === 'user'"
        class="master-list"
      >
        <li
          v-for="(section, i) in filteredUserGroups"
          :key="i"
        >
          <div class="group">
            {{ section.group }}
          </div>
          <ul>
            <li
              v-for="(u, i) in section.collection"
              :key="i"
              :class="{ selected: u.UserID === selectedUser.UserID }"
              class="child row user hoverable"
              @click="selectedUser = u"
            >
              <span
                v-if="u.LastName && u.FirstName"
                class="name"
              >
                {{ u.LastName }}, {{ u.FirstName }}
              </span>
              <span class="user-id">{{ u.UserID }}</span>
            </li>
          </ul>
        </li>
      </ul>

      <ul
        v-if="selectedType === 'funct'"
        class="master-list"
      >
        <li
          v-for="(cat, i) in permissionsForUser"
          :key="i"
        >
          <div class="group">
            {{ cat.Category }}
          </div>
          <ul>
            <li
              v-for="(role, i) in cat.Permissions"
              :key="i"
              :title="role.Description"
              :class="{ selected: role.PermissionName === selectedPerm.PermissionName }"
              class="child hoverable"
              @click="selectedPerm = role"
            >
              <span>{{ role.PermissionName }}</span>
            </li>
          </ul>
        </li>
      </ul>
    </div>

    <div class="column">
      <input
        v-model="filterText"
        placeholder="Filter Users"
        class="text-input"
      />
      <div
        v-if="selectedUser"
        id="current"
      >
        <b v-if="selectedType === 'user' && selectedUser.LastName && selectedUser.FirstName">
          Permissions for: {{ selectedUser.LastName }}, {{ selectedUser.FirstName }}
        </b>
        <b v-else-if="selectedType === 'user'">Permissions for: {{ selectedUser.UserID }}</b>
        <b v-else-if="selectedType === 'funct'">Assigning permission: {{ selectedPerm.PermissionName }}</b>
      </div>

      <div class="header">
        <span>Grant</span>
        <span>Deny</span>
      </div>

      <ul
        v-if="selectedType === 'user'"
        class="master-list"
      >
        <li
          v-for="(cat, i) in permissionsForUser"
          :key="i"
        >
          <div class="group">
            {{ cat.Category }}
          </div>
          <ul>
            <li
              v-for="(role, i) in cat.Permissions"
              :key="i"
              :title="role.Description"
              class="child row"
            >
              <div class="check-container">
                <input
                  v-model="role.grantAccess"
                  class="accept"
                  type="checkbox"
                  data-type="grant"
                  @change="grantAccess($event, role)"
                />
                <input
                  v-model="role.DenyAccess"
                  class="deny"
                  type="checkbox"
                  :disabled="selectedUser.IsGroup"
                  data-type="deny"
                  @change="grantAccess($event, role)"
                />
              </div>
              <span>{{ role.PermissionName }}</span>
            </li>
          </ul>
        </li>
      </ul>
      <ul
        v-if="selectedType === 'funct'"
        class="master-list"
      >
        <li
          v-for="(section, i) in usersForPermission"
          :key="i"
        >
          <div class="group">
            {{ section.group }}
          </div>
          <ul>
            <li
              v-for="(u, i) in section.collection"
              :key="i"
              class="child row user"
            >
              <div class="check-container">
                <input
                  v-model="u.grantAccess"
                  class="accept"
                  type="checkbox"
                  data-type="grant"
                  @change="grantAccess($event, u)"
                />
                <input
                  v-model="u.DenyAccess"
                  class="deny"
                  type="checkbox"
                  :disabled="u.IsGroup"
                  data-type="deny"
                  @change="grantAccess($event, u)"
                />
                <span
                  v-if="u.LastName && u.FirstName"
                  class="name"
                >
                  {{ u.LastName }}, {{ u.FirstName }}
                </span>
                <span
                  v-if="u.IsGroup"
                  class="user-id"
                >
                  {{ u.UserID }}
                </span>
                <!-- if group, display name here to align next to checkboxes -->
              </div>
              <div
                v-if="!u.IsGroup"
                class="user-id"
              >
                {{ u.UserID }}
              </div>
            </li>
          </ul>
        </li>
      </ul>
    </div>
  </div>
</template>

<script>
import Enumerable from 'linq';
import { notificationsMixin } from 'library/mixins/notifications';
import { httpMixin } from 'library/mixins/http';
import Multitoggle from '@baseball-systems/MultiToggle';

export default {
  mixins: [notificationsMixin, httpMixin],
  data() {
    return {
      userGroups: [],
      users: [],
      groups: [],
      permissions: [],
      access: [],
      selectedUser: null,
      selectedPerm: null,
      userGroups: [],
      filterText: '',
      selectedType: 'user',
      byOptions: [
        {
          val: 'user',
          label: 'By User',
        },
        {
          val: 'funct',
          label: 'By Function',
        },
      ],
    };
  },
  computed: {
    filteredUserGroups: function () {
      if (!this.userGroups) {
        return null;
      } else if (this.filterText.length === 0) {
        return this.userGroups;
      } else {
        return this.userGroups.map((g) => {
          return {
            group: g.group,
            collection: g.collection.filter((g) => {
              let ft = this.filterText.toLowerCase();
              return g.UserID.toLowerCase().includes(ft) || (g.FirstName && g.FirstName.toLowerCase().includes(ft));
            }),
          };
        });
      }
    },

    permissionsForUser: function () {
      let ret = [];
      if (this.selectedUser) {
        ret = Enumerable.from(this.access)
          .where(`x => x.UserID === '${this.selectedUser.UserID}'`)
          .orderBy('$.Category')
          .thenBy('$.PermissionName')
          .groupBy('$.Category', null, function (k, g) {
            return {
              Category: k,
              Permissions: g.getSource(),
            };
          })
          .toArray();
      }
      return ret;
    },
    usersForPermission: function () {
      const filterText = this.filterText.toLowerCase();

      let filtered = Enumerable.from(this.access)
        .where(`x =>  x.PermissionName === '${this.selectedPerm.PermissionName}'`)
        .where(
          `x => x.UserID.toLowerCase().includes('${filterText}') || (x.FirstName  !== null && x.FirstName.toLowerCase().includes('${filterText}'))`,
        )
        .orderBy('$.Category')
        .thenBy('$.PermissionName')
        .toArray();
      return this.returnGroupedUsers(filtered);
    },
  },
  created() {
    this.request('/dvsvc/SecUserPermissions').then((resp) => {
      if (resp.IsError) {
        this.notifyDanger('Error: SecUserPermissions service');
        return;
      }

      this.userGroups = Enumerable.from(resp.Users)
        .groupBy('$.IsGroup', null, (k, g) => {
          return {
            group: k ? 'Group' : 'Users',
            collection: Enumerable.from(g.getSource()).orderBy('x => !x.LastName ? x.UserID : x.LastName').toArray(),
          };
        })
        .orderBy('$.group')
        .toArray();

      this.groups = this.userGroups[1];
      this.users = this.userGroups[0];
      this.selectedUser = this.users.collection[0];

      this.permissions = Enumerable.from(resp.Permissions).orderBy('$.Category').thenBy('$.PermissionName').toArray();
      this.selectedPerm = this.permissions[0];
      this.access = resp.Access;
      // // Get permissions
    });
  },
  methods: {
    grantAccess: function (_e, _roleObj) {
      let type = _e.target.attributes['data-type'].value;
      let checked = _e.target.checked;
      switch (type) {
        case 'grant':
          _roleObj.grantAccess === checked;
          if (checked === true) {
            _roleObj.DenyAccess = false;
          }
          break;
        case 'deny':
          _roleObj.DenyAccess === checked;
          if (checked === true) {
            _roleObj.grantAccess = false;
          }
          break;
      }
      this.request('/dvsvc/SecUserPermissionsAccess', 'POST', _roleObj).then((resp) => {
        if (resp.IsError) {
          this.notifyDanger('Error: SecUserPermissionsAccess service');

          return;
        }
        this.notifySuccess('User Role Updated');
      });
    },
    returnGroupedUsers: function (_passedUsers) {
      return Enumerable.from(_passedUsers)
        .groupBy('$.IsGroup', null, (k, g) => {
          return {
            group: k ? 'Group' : 'Users',
            collection: Enumerable.from(g.getSource()).orderBy('x => !x.LastName ? x.UserID : x.LastName').toArray(),
          };
        })
        .orderBy('$.group')
        .toArray();
    },
  },
};
</script>

<style scoped lang="scss">
ul {
  padding-left: 0px;
  margin-top: 0px;
}
</style>
